// components/doctor-dashboard/DoctorStatsGrid.jsx
import React from "react";
import { Stethoscope, Clock, Calendar, FileText } from "lucide-react";

const Card = ({ icon, label, value, tone, onClick }) => {
  const clickable = typeof onClick === "function";

  return (
    <button
      type="button"
      className="stat-card"
      onClick={clickable ? onClick : undefined}
      style={{
        background: "#fff",
        border: "1px solid #eee",
        borderRadius: 12,
        padding: 14,
        transition: "border-color .15s ease, transform .15s ease",
        cursor: clickable ? "pointer" : "default",
        textAlign: "left",
      }}
    >
      <div className="stat-card__inner">
        <div className="stat-card__icon" data-tone={tone}>
          {icon}
        </div>
        <div className="stat-card__text">
          <p className="stat-card__label" title={label}>
            {label}
          </p>
          <p className="stat-card__value">{value ?? 0}</p>
        </div>
      </div>
    </button>
  );
};

const DoctorStatsGrid = ({
  stats = {},
  onClickToday,
  onClickPending,
  onClickTotal,
  onClickUploads,
}) => {
  const {
    todaysConfirmed = 0,
    pendingBookings = 0,
    totalBookings = 0,
    uploadsCount = 0,
  } = stats;

  return (
    <>
      <style>{`
        :root { --icon-size: 42px; }

        /* Grid */
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
          gap: 16px;
          margin-bottom: 20px;
        }

        /* Card base styles are now applied inline for clickable logic */
        .stat-card__inner {
          display:flex;
          align-items:center;
          gap: 12px;
          min-height: 92px;
        }
        .stat-card__icon {
          width: var(--icon-size);
          height: var(--icon-size);
          border-radius: 10px;
          display:flex;
          align-items:center;
          justify-content:center;
          color: #fff;
          flex: 0 0 var(--icon-size);
        }

        /* Solid professional tones (no gradients) */
        .stat-card__icon[data-tone="green"]  { background:#10b981; }
        .stat-card__icon[data-tone="amber"]  { background:#f59e0b; }
        .stat-card__icon[data-tone="blue"]   { background:#3b82f6; }
        .stat-card__icon[data-tone="violet"] { background:#8b5cf6; }

        .stat-card__text { min-width:0; }
        .stat-card__label {
          margin: 0 0 4px 0;
          font-size: 12.5px;
          font-weight: 600;
          color: #6b7280;
          letter-spacing: .2px;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .stat-card__value {
          margin: 0;
          font-weight: 800;
          font-size: 22px;
          line-height: 1.1;
          color: #111827;
        }

        @media (hover:hover) and (pointer:fine){
          .stat-card:hover {
            border-color: #e3e3e3;
            transform: translateY(-1px);
          }
        }

        /* Phones: keep equal size, but flat look */
        @media (max-width: 640px) {
          .stats-grid {
            display: flex;
            overflow-x: auto;
            padding: 2px 4px 10px;
            margin: 0 -8px 14px;
            gap: 10px;
            scroll-snap-type: x mandatory;
            -webkit-overflow-scrolling: touch;
          }
          .stats-grid { --card-w: 168px; --card-h: 110px; --icon-size: 36px; }
          .stat-card {
            flex: 0 0 var(--card-w);
            width: var(--card-w);
            height: var(--card-h);
            border-radius: 10px;
            padding: 12px;
            scroll-snap-align: start;
          }
          .stat-card__label { font-size: 11.5px; }
          .stat-card__value { font-size: 20px; }
          .stats-grid::-webkit-scrollbar { height: 6px; }
          .stats-grid::-webkit-scrollbar-thumb {
            background: rgba(0,0,0,.12);
            border-radius: 999px;
          }
        }
      `}</style>

      <div className="stats-grid">
        <Card
          icon={<Stethoscope style={{ width: 20, height: 20 }} />}
          label="Today's Appointments"
          value={todaysConfirmed}
          tone="green"
         
        />
        <Card
          icon={<Clock style={{ width: 20, height: 20 }} />}
          label="Pending Approval"
          value={pendingBookings}
          tone="amber"
          onClick={onClickPending}
        />
        <Card
          icon={<Calendar style={{ width: 20, height: 20 }} />}
          label="Total Bookings"
          value={totalBookings}
          tone="blue"
          onClick={onClickTotal}
        />
        <Card
          icon={<FileText style={{ width: 20, height: 20 }} />}
          label="Research Papers"
          value={uploadsCount}
          tone="violet"
          onClick={onClickUploads}
        />
      </div>
    </>
  );
};

export default React.memo(DoctorStatsGrid);
